// src/pages/Booking.js
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  Grid,
  TextField,
  Alert,
  Snackbar,
  styled,
  CircularProgress,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addDays } from 'date-fns';
import { 
  sendBookingNotification, 
  sendPaymentVerification,
  sendBookingConfirmation 
} from '../services/whatsappService';

const steps = ['Select Dates & Rooms', 'Guest Information', 'Payment'];

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: '24px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '50px',
  padding: '15px 40px',
  fontSize: '1.1rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(46, 125, 50, 0.2)',
  },
  transition: 'all 0.3s ease',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '15px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '16px',
  },
}));

// Validation Functions
const validatePhone = (phone) => {
  const phoneRegex = /^[0-9]{10}$/;
  return phoneRegex.test(phone);
};

const validateName = (name) => {
  const nameRegex = /^[a-zA-Z\s]*$/;
  return nameRegex.test(name);
};

function DateAndRoomSelection({ bookingData, setBookingData, errors }) {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <DatePicker
            label="Check-in Date"
            value={bookingData.checkIn}
            onChange={(newValue) => setBookingData({ ...bookingData, checkIn: newValue })}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                fullWidth
                error={Boolean(errors.checkIn)}
                helperText={errors.checkIn}
              />
            )}
            minDate={new Date()}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DatePicker
            label="Check-out Date"
            value={bookingData.checkOut}
            onChange={(newValue) => setBookingData({ ...bookingData, checkOut: newValue })}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                fullWidth
                error={Boolean(errors.checkOut)}
                helperText={errors.checkOut}
              />
            )}
            minDate={addDays(bookingData.checkIn || new Date(), 1)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StyledTextField
            fullWidth
            label="Number of Garden View Rooms"
            type="text"
            value={bookingData.rooms}
            onChange={(e) => {
              const value = e.target.value === '' ? '' : parseInt(e.target.value);
              if (value === '' || (!isNaN(value) && value >= 0 && value <= 4)) {
                setBookingData({ ...bookingData, rooms: value });
              }
            }}
            error={Boolean(errors.rooms)}
            helperText={errors.rooms || "Maximum 4 rooms available"}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StyledTextField
            fullWidth
            label="Adults"
            type="text"
            value={bookingData.adults}
            onChange={(e) => {
              const value = e.target.value === '' ? '' : parseInt(e.target.value);
              if (value === '' || (!isNaN(value) && value >= 0 && value <= (bookingData.rooms * 2))) {
                setBookingData({ ...bookingData, adults: value });
              }
            }}
            error={Boolean(errors.adults)}
            helperText={errors.adults || `Maximum ${bookingData.rooms * 2} adults (2 per room)`}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StyledTextField
            fullWidth
            label="Children"
            type="text"
            value={bookingData.children}
            onChange={(e) => {
              const value = e.target.value === '' ? '' : parseInt(e.target.value);
              if (value === '' || (!isNaN(value) && value >= 0 && value <= 4)) {
                setBookingData({ ...bookingData, children: value });
              }
            }}
            error={Boolean(errors.children)}
            helperText={errors.children || "Ages 12 and under (Maximum 4)"}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
}


function GuestDetails({ bookingData, setBookingData, errors }) {
  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
    setBookingData({ ...bookingData, phone: value });
  };

  const handleNameChange = (e, field) => {
    const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
    setBookingData({ ...bookingData, [field]: value });
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={6}>
        <StyledTextField
          fullWidth
          label="First Name"
          value={bookingData.firstName}
          onChange={(e) => handleNameChange(e, 'firstName')}
          error={Boolean(errors.firstName)}
          helperText={errors.firstName}
          required
          InputProps={{
            sx: { fontSize: '1.1rem' }
          }}
          placeholder="Enter first name"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <StyledTextField
          fullWidth
          label="Last Name"
          value={bookingData.lastName}
          onChange={(e) => handleNameChange(e, 'lastName')}
          error={Boolean(errors.lastName)}
          helperText={errors.lastName}
          required
          InputProps={{
            sx: { fontSize: '1.1rem' }
          }}
          placeholder="Enter last name"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <StyledTextField
          fullWidth
          label="Email (Optional)"
          type="email"
          value={bookingData.email}
          onChange={(e) => setBookingData({ ...bookingData, email: e.target.value })}
          error={Boolean(errors.email)}
          helperText={errors.email}
          InputProps={{
            sx: { fontSize: '1.1rem' }
          }}
          placeholder="example@email.com"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <StyledTextField
          fullWidth
          label="Phone"
          value={bookingData.phone}
          onChange={handlePhoneChange}
          error={Boolean(errors.phone)}
          helperText={errors.phone || "Enter 10 digit mobile number"}
          required
          InputProps={{
            sx: { fontSize: '1.1rem' }
          }}
          placeholder="10 digit mobile number"
        />
      </Grid>
      <Grid item xs={12}>
        <StyledTextField
          fullWidth
          label="Special Requests (Optional)"
          multiline
          rows={4}
          value={bookingData.specialRequests}
          onChange={(e) => setBookingData({ ...bookingData, specialRequests: e.target.value })}
          InputProps={{
            sx: { fontSize: '1.1rem' }
          }}
          placeholder="Any special requests or preferences"
        />
      </Grid>
    </Grid>
  );
}
// PaymentStep Component
function PaymentStep({ bookingData, isSubmitting, onPaymentComplete, setSnackbar }) {
  const [showQR, setShowQR] = useState(false);
  const [paymentProof, setPaymentProof] = useState(null);
  const [utrNumber, setUtrNumber] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Utility function to check if a date is a weekend
  const isWeekendDay = (date) => {
    const day = new Date(date).getDay();
    return day === 0 || day === 6; // 0 is Sunday, 6 is Saturday
  };

  // Function to get all dates between check-in and check-out
  const getDatesInRange = (startDate, endDate) => {
    const dates = [];
    let currentDate = new Date(startDate);
    const end = new Date(endDate);
    end.setDate(end.getDate() - 1);

    while (currentDate <= end) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  // Calculate amount with weekend pricing
  const calculateAmount = () => {
    const regularPrice = 2500;
    const weekendPrice = 3000;
    
    const dates = getDatesInRange(bookingData.checkIn, bookingData.checkOut);
    let weekendDays = 0;
    let regularDays = 0;

    dates.forEach(date => {
      if (isWeekendDay(date)) {
        weekendDays++;
      } else {
        regularDays++;
      }
    });

    const regularTotal = regularDays * regularPrice;
    const weekendTotal = weekendDays * weekendPrice;
    const totalAmount = (regularTotal + weekendTotal) * bookingData.rooms;

    return {
      totalAmount,
      breakdown: {
        regularDays,
        weekendDays,
        regularPrice,
        weekendPrice,
        regularTotal,
        weekendTotal
      }
    };
  };

  const { totalAmount, breakdown } = calculateAmount();
  const upiId = "johnby1971-1@okicici";
  const merchantName = "East Fort Resorts";
  const [transactionId] = useState(`TXN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  // Generate UPI payment link
  const generateUPILink = () => {
    const formattedMerchantName = merchantName.replace(/\s+/g, '_');
    const upiUrl = new URLSearchParams({
      pa: upiId,
      pn: formattedMerchantName,
      tn: `Booking_${transactionId}`,
      am: totalAmount.toString(),
      cu: 'INR',
    }).toString();

    return `upi://pay?${upiUrl}`;
  };

  // Check if device is mobile
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Handle direct UPI app opening for mobile
  const handleDirectPayment = () => {
    const upiLink = generateUPILink();
    window.location.href = upiLink;
  };

  // Handle payment verification
  const handleVerifyPayment = async () => {
    if (!paymentProof && !utrNumber) {
      setError('Please upload payment screenshot and enter UTR number');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Send verification with all required data
      const verificationResult = await sendPaymentVerification({
        ...bookingData,
        amount: totalAmount,
        utrNumber,
        transactionId,
        paymentProof
      });

      if (verificationResult.success) {
        setVerificationStatus('success');
        
        // Send confirmation to customer
        await sendBookingConfirmation({
          ...bookingData,
          amount: totalAmount,
          paymentId: verificationResult.paymentId
        });
        
        setSnackbar({
          open: true,
          message: 'Payment details submitted successfully! We will verify and confirm your booking shortly.',
          severity: 'success'
        });

        onPaymentComplete();
      } else {
        throw new Error(verificationResult.error || 'Failed to send payment verification');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationStatus('failed');
      setError(error.message || 'Verification failed. Please contact support.');
      
      setSnackbar({
        open: true,
        message: 'Failed to submit payment details. Please try again or contact support.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Booking Summary
        </Typography>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body1">
            Number of Rooms: {bookingData.rooms}
          </Typography>
          <Typography variant="body1">
            Check-in: {new Date(bookingData.checkIn).toLocaleDateString()}
          </Typography>
          <Typography variant="body1">
            Check-out: {new Date(bookingData.checkOut).toLocaleDateString()}
          </Typography>
  
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Price Breakdown
            </Typography>
            <Typography variant="body2">
              Regular Days: {breakdown.regularDays} days × ₹{breakdown.regularPrice} = ₹{breakdown.regularTotal}
            </Typography>
            <Typography variant="body2">
              Weekend Days: {breakdown.weekendDays} days × ₹{breakdown.weekendPrice} = ₹{breakdown.weekendTotal}
            </Typography>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Total Amount: ₹{totalAmount}
            </Typography>
          </Box>
        </Box>
  
        {/* Payment Methods Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Payment Methods
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {isMobile() && (
              <StyledButton
                variant="contained"
                color="primary"
                onClick={handleDirectPayment}
                disabled={loading}
                fullWidth
              >
                Pay with UPI App
              </StyledButton>
            )}
            <StyledButton
              variant={isMobile() ? "outlined" : "contained"}
              color="primary"
              onClick={() => setShowQR(true)}
              disabled={loading}
              fullWidth
            >
              {isMobile() ? "Show QR Code" : "Show Payment QR"}
            </StyledButton>
          </Box>
        </Box>
  
        {/* QR Code Dialog */}
        {showQR && (
          <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <QRCodeSVG value={generateUPILink()} size={256} />
            <Typography variant="body1" gutterBottom>
              Amount: ₹{totalAmount}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              UPI ID: {upiId}
            </Typography>
            <StyledButton
              variant="outlined"
              color="primary"
              onClick={() => setShowQR(false)}
              fullWidth
            >
              Hide QR Code
            </StyledButton>
          </Box>
        )}
  
        {/* Payment Verification Section - Always visible */}
        <Box sx={{ width: '100%', mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Verify Your Payment
          </Typography>
  
          <Alert severity="info" sx={{ mb: 3 }}>
            After making the payment, please provide:
            1. Screenshot of payment confirmation
            2. UTR number from your UPI payment history
          </Alert>
  
          <Box sx={{ mb: 3 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="payment-proof"
              type="file"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file && file.size <= 5 * 1024 * 1024) {
                  setPaymentProof(file);
                } else {
                  alert('Please select an image less than 5MB');
                }
              }}
            />
            <label htmlFor="payment-proof">
              <StyledButton
                variant="outlined"
                component="span"
                fullWidth
                sx={{ mb: 2 }}
              >
                Upload Payment Screenshot
              </StyledButton>
            </label>
            
            {paymentProof && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Screenshot uploaded: {paymentProof.name}
              </Typography>
            )}
          </Box>
  
          <StyledTextField
            fullWidth
            label="UTR Number"
            value={utrNumber}
            onChange={(e) => setUtrNumber(e.target.value)}
            sx={{ mb: 3 }}
            placeholder="Enter UTR number from payment"
            helperText="Find this in your UPI app payment history"
          />
  
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
  
          <StyledButton
            variant="contained"
            color="primary"
            onClick={handleVerifyPayment}
            disabled={loading || (!paymentProof && !utrNumber)}
            fullWidth
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Submit Payment Verification'
            )}
          </StyledButton>
  
          {verificationStatus === 'success' && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Payment details submitted successfully! We will verify and confirm your booking shortly.
            </Alert>
          )}
  
          {verificationStatus === 'failed' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to submit payment details. Please contact support.
            </Alert>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}
function Booking() {
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingData, setBookingData] = useState({
    checkIn: null,
    checkOut: null,
    rooms: 1,
    adults: 1,
    children: 0,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: '',
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const validateStep = () => {
    const newErrors = {};
  
    switch (activeStep) {
      case 0:
        if (!bookingData.checkIn) newErrors.checkIn = 'Check-in date is required';
        if (!bookingData.checkOut) newErrors.checkOut = 'Check-out date is required';
        if (!bookingData.rooms || bookingData.rooms < 1) newErrors.rooms = 'At least 1 room is required';
        if (!bookingData.adults || bookingData.adults < 1) newErrors.adults = 'At least 1 adult is required';
        break;
      case 1:
        if (!bookingData.firstName?.trim()) {
          newErrors.firstName = 'First name is required';
        } else if (!validateName(bookingData.firstName)) {
          newErrors.firstName = 'Only letters and spaces allowed';
        }
  
        if (!bookingData.lastName?.trim()) {
          newErrors.lastName = 'Last name is required';
        } else if (!validateName(bookingData.lastName)) {
          newErrors.lastName = 'Only letters and spaces allowed';
        }
  
        if (!bookingData.phone) {
          newErrors.phone = 'Phone number is required';
        } else if (!validatePhone(bookingData.phone)) {
          newErrors.phone = 'Please enter a valid 10-digit phone number';
        }
  
        if (bookingData.email && !/\S+@\S+\.\S+/.test(bookingData.email)) {
          newErrors.email = 'Please enter a valid email';
        }
        break;
      default:
        break;
    }
  
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => validateStep() && setActiveStep((prev) => prev + 1);
  
  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (validateStep()) {
      try {
        setIsSubmitting(true);
        
        const formattedBookingData = {
          ...bookingData,
          checkIn: bookingData.checkIn instanceof Date 
            ? bookingData.checkIn 
            : new Date(bookingData.checkIn),
          checkOut: bookingData.checkOut instanceof Date 
            ? bookingData.checkOut 
            : new Date(bookingData.checkOut),
          phone: bookingData.phone.startsWith('+') 
            ? bookingData.phone 
            : `+91${bookingData.phone}`
        };
  
        console.log('Submitting formatted booking data:', formattedBookingData);
  
        const notificationResult = await sendBookingNotification(formattedBookingData);
        console.log('Notification result:', notificationResult);
  
        if (notificationResult.success) {
          setSnackbar({
            open: true,
            message: 'Booking confirmed! We will contact you shortly.',
            severity: 'success',
          });
  
          setBookingData({
            checkIn: null,
            checkOut: null,
            rooms: 1,
            adults: 1,
            children: 0,
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            specialRequests: '',
          });
  
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
        } else {
          throw new Error(notificationResult.error || 'Failed to send booking notification');
        }
      } catch (error) {
        console.error('Booking error:', error);
        
        setSnackbar({
          open: true,
          message: error.message || 'There was an error processing your booking. Please try again.',
          severity: 'error',
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Box sx={{ 
      py: 12, 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
    }}>
      <Container maxWidth="md">
        <Typography 
          variant="h2" 
          component="h1" 
          textAlign="center" 
          gutterBottom
          sx={{ 
            mb: 8,
            fontWeight: 700,
            background: 'linear-gradient(45deg, #1a237e 30%, #4caf50 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {activeStep === 2 ? 'Complete Payment' : 'Book Your Perfect Stay'}
        </Typography>

        <Stepper 
          activeStep={activeStep} 
          sx={{ 
            mb: 6,
            '& .MuiStepLabel-root .Mui-completed': {
              color: 'primary.main',
            },
            '& .MuiStepLabel-root .Mui-active': {
              color: 'primary.main',
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <StyledPaper>
          {activeStep === 0 ? (
            <DateAndRoomSelection 
              bookingData={bookingData} 
              setBookingData={setBookingData} 
              errors={errors} 
            />
          ) : activeStep === 1 ? (
            <GuestDetails 
              bookingData={bookingData} 
              setBookingData={setBookingData} 
              errors={errors} 
            />
          ) : (
            <PaymentStep 
              bookingData={bookingData}
              isSubmitting={isSubmitting}
              onPaymentComplete={handleSubmit}
              setSnackbar={setSnackbar}
            />
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
            {activeStep !== 0 && (
              <StyledButton 
                onClick={handleBack}
                variant="outlined"
                disabled={isSubmitting}
              >
                Back
              </StyledButton>
            )}
            {activeStep !== 2 && (
              <StyledButton
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
              >
                Next
              </StyledButton>
            )}
          </Box>
        </StyledPaper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%', fontSize: '1.1rem' }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}

export default Booking;